import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { TalentService } from '../talent.service';
import { UntypedFormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { debounceTime, switchMap, startWith } from 'rxjs/operators';

export interface TestSkill {
  id: number;
  skill_name: string;
  created_at?: string;
}

@Component({
    selector: 'app-ai-test-generation',
    templateUrl: './ai-test-generation.component.html',
    styleUrls: ['./ai-test-generation.component.css'],
    standalone: false
})
export class AiTestGenerationComponent implements OnInit {
  skills: TestSkill[] = [];
  selectedSkills: TestSkill[] = [];
  skillInputControl = new UntypedFormControl('');
  filteredSkills: Observable<TestSkill[]>;
  skillSearchTerm: string = '';
  pendingSkillName: string = ''; // Store input value before autocomplete changes it
  numMultipleChoice: number = 5;
  numFreeForm: number = 0;
  difficultyLevel: string = 'intermediate';
  timeLimitMinutes: number = 0; // Auto-calculated, but editable
  testName: string = '';
  testDescription: string = '';
  allowAiChat: boolean = false; // Whether candidates get an in-assessment AI chat assistant
  
  isGenerating: boolean = false;
  isSaving: boolean = false;
  isLoading: boolean = false;
  regeneratingQuestionIndex: number | null = null; // Track which question is regenerating
  generatedQuestions: any = null;
  errorMessage: string = '';
  successMessage: string = '';
  
  // Edit mode
  editMode: boolean = false;
  testId: number | null = null;

  constructor(
    private talentService: TalentService,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    // Setup autocomplete filtering
    this.filteredSkills = this.skillInputControl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      switchMap(value => {
        // Store the actual typed value before autocomplete changes it
        if (typeof value === 'string') {
          this.pendingSkillName = value;
        }
        return this.filterSkills(value || '');
      })
    );
  }

  ngOnInit() {
    this.loadSkills();
    
    // Check if we're in edit mode
    this.route.queryParams.subscribe(params => {
      if (params['edit'] && params['testId']) {
        this.editMode = true;
        this.testId = parseInt(params['testId']);
        this.loadTestForEdit(this.testId);
      }
    });
  }

  loadSkills() {
    this.talentService.getTestSkills().subscribe(
      (skills) => {
        this.skills = skills;
      },
      (error) => {
        console.error('Error loading skills:', error);
        this.errorMessage = 'Failed to load test skills';
      }
    );
  }

  filterSkills(searchTerm: string): Observable<TestSkill[]> {
    if (!searchTerm || searchTerm.length < 2) {
      // Return already selected skills + top skills if no search
      return new Observable(observer => {
        observer.next(this.skills.slice(0, 10));
        observer.complete();
      });
    }
    
    // Search via API
    return this.talentService.getTestSkills(searchTerm);
  }

  onSkillSelected(event: any) {
    const value = event.option.value;
    
    if (value === '__ADD_NEW__') {
      // User clicked "Add new skill" - use stored value before autocomplete changed it
      const skillName = this.pendingSkillName;
      this.skillInputControl.setValue('');
      this.addNewSkill(skillName);
    } else {
      // User selected existing skill
      this.selectSkill(value);
    }
  }

  selectSkill(skill: TestSkill) {
    // Check if already selected
    if (!this.selectedSkills.find(s => s.id === skill.id)) {
      this.selectedSkills.push(skill);
    }
    this.skillInputControl.setValue('');
  }

  removeSkill(skill: TestSkill) {
    this.selectedSkills = this.selectedSkills.filter(s => s.id !== skill.id);
  }

  addNewSkill(skillName: string) {
    if (!skillName || skillName.trim().length === 0) {
      return;
    }

    skillName = skillName.trim();

    this.talentService.addTestSkill(skillName).subscribe(
      (response) => {
        if (response.status === 'success' && response.skill) {
          // Add to selected skills
          this.selectSkill(response.skill);
          // Refresh skills list
          this.loadSkills();
          this.skillInputControl.setValue('');
        } else {
          this.errorMessage = response.message || 'Failed to add skill';
        }
      },
      (error) => {
        console.error('Error adding skill:', error);
        this.errorMessage = 'Failed to add new skill';
      }
    );
  }

  loadTestForEdit(testId: number) {
    this.isLoading = true;
    this.talentService.getCandidateTestDetail(testId).subscribe(
      (test) => {
        this.isLoading = false;
        // Convert skills array to TestSkill objects
        if (test.skills && Array.isArray(test.skills)) {
          this.selectedSkills = test.skills;
        }
        this.difficultyLevel = test.difficulty || 'intermediate';
        this.timeLimitMinutes = test.time_limit_minutes;
        this.generatedQuestions = test.questions;
        this.testName = test.name || '';
        this.testDescription = test.description || '';
        this.allowAiChat = !!test.allow_ai_chat;
      },
      (error) => {
        this.isLoading = false;
        console.error('Error loading test:', error);
        this.errorMessage = 'Failed to load test for editing';
      }
    );
  }

  generateTest() {
    if (!this.validateForm()) {
      return;
    }

    this.isGenerating = true;
    this.errorMessage = '';
    this.successMessage = '';
    this.generatedQuestions = null;

    const params = {
      skill_ids: this.selectedSkills.map(s => s.id),
      num_multiple_choice: this.numMultipleChoice,
      num_free_form: this.numFreeForm,
      difficulty_level: this.difficultyLevel,
      allow_ai_chat: this.allowAiChat
    };

    this.talentService.generateCandidateTest(params).subscribe(
      (response) => {
        console.log('Enqueue response:', response);
        if (response.status === 'processing' && response.job_id) {
          // Generation runs as a background job; poll for the result.
          this.pollTestGenerationStatus(response.job_id);
        } else if (response.status === 'success') {
          // Backwards compatibility if the API ever returns results directly
          this.handleGeneratedResult(response);
        } else {
          this.isGenerating = false;
          this.errorMessage = response.message || 'Failed to generate test';
        }
      },
      (error) => {
        this.isGenerating = false;
        console.error('Error generating test:', error);
        this.errorMessage = 'Failed to generate test. Please try again.';
      }
    );
  }

  private pollTestGenerationStatus(jobId: string, attempt: number = 0) {
    const maxAttempts = 120; // ~4 min at 2s interval
    const intervalMs = 2000;

    if (attempt >= maxAttempts) {
      this.isGenerating = false;
      this.errorMessage = 'Test generation timed out. Please try again.';
      return;
    }

    this.talentService.getTestGenerationStatus(jobId).subscribe(
      (response) => {
        if (response.status === 'success') {
          this.handleGeneratedResult(response);
        } else if (response.status === 'processing') {
          setTimeout(() => this.pollTestGenerationStatus(jobId, attempt + 1), intervalMs);
        } else {
          this.isGenerating = false;
          this.errorMessage = response.message || 'Failed to generate test';
        }
      },
      (error) => {
        this.isGenerating = false;
        console.error('Error checking test generation status:', error);
        this.errorMessage = 'Failed to generate test. Please try again.';
      }
    );
  }

  private handleGeneratedResult(response: any) {
    this.isGenerating = false;
    this.generatedQuestions = response.questions;
    // Auto-calculate time limit from estimated times
    this.timeLimitMinutes = this.getTotalEstimatedTime();
    this.successMessage = `Successfully generated ${response.count} questions!`;
    console.log('Generated questions with auto time limit:', this.timeLimitMinutes);
  }

  regenerateQuestion(index: number) {
    if (!this.generatedQuestions || !this.generatedQuestions[index]) {
      return;
    }

    this.regeneratingQuestionIndex = index;
    this.errorMessage = '';
    this.successMessage = '';

    const params = {
      skill_ids: this.selectedSkills.map(s => s.id),
      existing_questions: this.generatedQuestions,
      question_index: index,
      difficulty_level: this.difficultyLevel
    };

    this.talentService.regenerateSingleQuestion(params).subscribe(
      (response) => {
        this.regeneratingQuestionIndex = null;
        if (response.status === 'success') {
          // Replace the question at the index
          this.generatedQuestions[index] = response.question;
          // Recalculate time limit
          this.timeLimitMinutes = this.getTotalEstimatedTime();
          this.successMessage = `Question ${index + 1} regenerated successfully!`;
          setTimeout(() => { this.successMessage = ''; }, 3000);
        } else {
          this.errorMessage = response.message || 'Failed to regenerate question';
        }
      },
      (error) => {
        this.regeneratingQuestionIndex = null;
        console.error('Error regenerating question:', error);
        this.errorMessage = 'Failed to regenerate question. Please try again.';
      }
    );
  }

  saveTest() {
    if (!this.generatedQuestions || this.generatedQuestions.length === 0) {
      this.errorMessage = 'No questions to save';
      return;
    }

    this.isSaving = true;
    this.errorMessage = '';

    const testData = {
      skill_ids: this.selectedSkills.map(s => s.id),
      time_limit_minutes: this.timeLimitMinutes,
      difficulty_level: this.difficultyLevel,
      questions: this.generatedQuestions,
      name: this.testName || null,
      description: this.testDescription || null,
      allow_ai_chat: this.allowAiChat
    };

    const saveObservable = this.editMode && this.testId
      ? this.talentService.updateCandidateTest(this.testId, testData)
      : this.talentService.saveCandidateTest(testData);

    saveObservable.subscribe(
      (response) => {
        this.isSaving = false;
        if (response.status === 'success') {
          this.successMessage = this.editMode ? 'Test updated successfully!' : 'Test saved successfully!';
          setTimeout(() => {
            this.router.navigate(['/ai-test-detail', response.test.id]);
          }, 1000);
        } else {
          this.errorMessage = response.message || 'Failed to save test';
        }
      },
      (error) => {
        this.isSaving = false;
        console.error('Error saving test:', error);
        this.errorMessage = 'Failed to save test. Please try again.';
      }
    );
  }

  regenerateTest() {
    this.generatedQuestions = null;
    this.generateTest();
  }

  cancelGeneration() {
    this.router.navigate(['/ai-test-list']);
  }

  validateForm(): boolean {
    if (this.selectedSkills.length === 0) {
      this.errorMessage = 'Please select at least one skill';
      return false;
    }
    if (this.numMultipleChoice < 0 || this.numFreeForm < 0) {
      this.errorMessage = 'Number of questions cannot be negative';
      return false;
    }
    if (this.numMultipleChoice === 0 && this.numFreeForm === 0) {
      this.errorMessage = 'Please specify at least one question';
      return false;
    }
    return true;
  }

  getSkillNames(): string {
    if (!this.selectedSkills || this.selectedSkills.length === 0) {
      return '';
    }
    return this.selectedSkills
      .map(s => s.skill_name)
      .join(', ');
  }

  getOptionLetter(index: number): string {
    return String.fromCharCode(65 + index);
  }

  getTotalEstimatedTime(): number {
    if (!this.generatedQuestions || this.generatedQuestions.length === 0) {
      return 0;
    }
    return this.generatedQuestions.reduce((total, question) => {
      return total + (question.estimated_time_minutes || 0);
    }, 0);
  }
}
